import { useState, useEffect } from 'react';
import { apiFetch } from '../App';

const BOOKMAKER_LABELS = {
  betway_bw: 'BW Bookmaker',
};

function StatCard({ label, value, sub, color }) {
  return (
    <div className="stat-card" style={{ '--accent-color': color || 'var(--accent)' }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function MarketBar({ market, count, percentage, max }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 13, color: 'var(--text2)' }}>{market}</span>
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>{count} ({percentage}%)</span>
      </div>
      <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${(count / max) * 100}%`,
          background: 'var(--accent)',
          borderRadius: 3,
          transition: 'width 0.5s ease',
        }} />
      </div>
    </div>
  );
}

function TierCard({ label, count, total, color }) {
  const pct = total ? ((count / total) * 100).toFixed(1) : 0;
  return (
    <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: '12px 14px' }}>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, color }}>
        {count}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{pct}% of winners</div>
    </div>
  );
}

export default function WinnerIntelligence() {
  const [analytics, setAnalytics] = useState(null);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [width, setWidth] = useState(window.innerWidth);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

    useEffect(() => {
    Promise.all([
      apiFetch('/big-winners/analytics'),
      apiFetch('/big-winners/recent?limit=20'),
      apiFetch('/big-winners/insights'),
    ]).then(([analyticsData, winnersData, insightsData]) => {
      setAnalytics(analyticsData);
      setWinners(winnersData.winners || []);
      setInsights(insightsData.insights || null);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const isMobile = width < 768;

  function formatAmount(amount) {
    if (amount >= 10000) return `P${(amount / 1000).toFixed(1)}k`;
    return `P${amount?.toFixed(2)}`;
  }

  function formatDate(date) {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-BW', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: 80 }} />)}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header */}
      <div style={{
        background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.15)',
        borderRadius: 'var(--radius)', padding: '16px 20px',
        display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <div style={{ fontSize: 24, flexShrink: 0 }}>🏆</div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15 }}>Winner Intelligence</div>
            <span className="pill pill-amber" style={{ fontSize: 9 }}>BW BOOKMAKERS</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6 }}>
            Analytics from {analytics?.totalWinners || 0} real winning slips across BW bookmakers. Discover what markets, legs and strategies produce the biggest wins.
          </div>
        </div>
      </div>

      {/* Key Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 10 }}>
        <StatCard label="Total Winners Analysed" value={analytics?.totalWinners || 0} sub="Across BW bookmakers" color="var(--accent)"/>
        <StatCard label="Avg Legs Per Win" value={analytics?.avgLegs || 0} sub="Sweet spot: 6-10 legs" color="#f59e0b"/>
        <StatCard label="Avg Winning Return" value={formatAmount(analytics?.avgReturn)} sub={`From avg P${analytics?.avgStake?.toFixed(0)} stake`} color="var(--success)"/>
        <StatCard label="Biggest Win Ever" value={formatAmount(analytics?.maxReturn)} sub="Single winning slip" color="#ec4899"/>
      </div>

      {/* Markets + Legs Distribution */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>

        {/* Top Markets */}
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
            📊 Top Markets in Winning Slips
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
            Which bet types appear most in big wins
          </div>
          {analytics?.topMarkets?.map((m, i) => (
            <MarketBar
              key={m.market}
              market={m.market}
              count={m.count}
              percentage={m.percentage}
              max={analytics.topMarkets[0].count}
            />
          ))}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Win Tiers */}
          <div className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
              💰 Win Amount Distribution
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
              How big are the wins?
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8 }}>
              {analytics?.winTiers?.map((tier, i) => (
                <TierCard
                  key={tier.label}
                  label={tier.label}
                  count={tier.count}
                  total={analytics.totalWinners}
                  color={i === 0 ? 'var(--accent)' : i === 1 ? '#f59e0b' : i === 2 ? '#ec4899' : '#8b5cf6'}
                />
              ))}
            </div>
          </div>

          {/* Legs Distribution */}
          <div className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
              🎯 Legs Per Winning Slip
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
              How many legs do winners typically use?
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8 }}>
              {analytics?.legsDistribution?.map((leg, i) => (
                <TierCard
                  key={leg.label}
                  label={leg.label}
                  count={leg.count}
                  total={analytics.totalWinners}
                  color={i === 1 ? 'var(--accent)' : i === 2 ? '#f59e0b' : 'var(--text2)'}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

        {/* Key Insights */}
      <div className="card" style={{ padding: '16px 20px' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
          💡 AI-Generated Insights
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
          Updated daily based on real winning slip data
        </div>
        {!insights ? (
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text3)', fontSize: 13 }}>
            Insights generating... check back after the next daily update.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 12 }}>
            {insights.map((insight, i) => (
              <div key={i} style={{ background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: '14px 16px' }}>
                <div style={{ fontSize: 20, marginBottom: 8 }}>{insight.icon}</div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{insight.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>{insight.desc}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Big Winners */}
      <div className="card">
        <div className="section-header">
          <div className="section-title">🏆 Recent Big Winners</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>Sorted by return amount</div>
        </div>
        {winners.map((w, i) => (
          <div
            key={w.betslipId}
            onClick={() => setSelectedWinner(selectedWinner?.betslipId === w.betslipId ? null : w)}
            style={{
              padding: isMobile ? '12px 14px' : '14px 20px',
              borderBottom: '1px solid var(--border)',
              cursor: 'pointer',
              background: selectedWinner?.betslipId === w.betslipId ? 'rgba(0,229,176,0.04)' : 'transparent',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, color: 'var(--success)' }}>
                    {formatAmount(w.returnAmount)}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>from P{w.wagerAmount}</span>
                  <span className="pill pill-teal" style={{ fontSize: 9 }}>{w.numberOfBets} legs</span>
                  <span className="pill pill-blue" style={{ fontSize: 9 }}>{w.totalOdds?.toFixed(2)}x</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                  {formatDate(w.settlementDate)} · Top markets: {w.topMarkets?.join(', ')}
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', flexShrink: 0 }}>
                {selectedWinner?.betslipId === w.betslipId ? '▲' : '▼'}
              </div>
            </div>

            {/* Expanded selections */}
            {selectedWinner?.betslipId === w.betslipId && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Winning selections:
                </div>
                {w.selections?.map((sel, j) => (
                  <div key={j} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 10px', background: 'var(--bg3)', borderRadius: 6, fontSize: 12,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ color: 'var(--text2)', fontWeight: 500 }}>{sel.event}</span>
                      <span style={{ color: 'var(--text3)', marginLeft: 8 }}>{sel.market} — {sel.outcome}</span>
                    </div>
                    <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--accent)', flexShrink: 0, marginLeft: 8 }}>
                      {sel.odds}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Disclaimer */}
      <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', padding: '0 20px', lineHeight: 1.6 }}>
        ℹ️ Analytics based on publicly available winning slip data. Past performance does not guarantee future results. Always bet responsibly.
      </div>
    </div>
  );
}